import Badge from '@axa-japan/design-system-react/Badge';
import Button from '@axa-japan/design-system-react/Button';
import Heading from '@axa-japan/design-system-react/Heading';
import { ArrowIcon } from '@axa-japan/design-system-react/Icons';
import Message from '@axa-japan/design-system-react/Message';
import StatusModal from '@axa-japan/design-system-react/StatusModal';
import Text from '@axa-japan/design-system-react/Text';
import TextField from '@axa-japan/design-system-react/TextField';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useReducer, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { isValidJapaneseMobileNo } from '../../business/isValidJapaneseMobileNo';
import useSendAccess from '../../hooks/useSendAccess';
import { queryClient } from '../../libs/queryClient';
import { useUserContext } from '../../providers/UserProvider';
import { AUTH_TYPE } from '../../types/auth';
import { ERROR_CODE } from '../../types/response';
import { DOCUMENT_UPLOAD_PROGRESS } from '../../types/session';
import { addJPCountryCode, formatPhoneNumberDisplay } from '../../utils/fileUtils';

const RETRY_DELAY = 5;

type MODAL_TYPE = 'send' | 'retry';
type SMS_STATUS = 'success' | 'error';

type State = {
  isRetry: boolean;
  btnLabel: string;
};

type Action = { type: 'RESET' } | { type: 'TOGGLE_RETRY' } | { type: 'SET_BTN_LABEL'; payload: string };

const initialState: State = {
  isRetry: false,
  btnLabel: '送信',
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'TOGGLE_RETRY':
      return {
        ...state,
        isRetry: !state.isRetry,
      };
    case 'SET_BTN_LABEL':
      return { ...state, btnLabel: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

type SMSSendingFormForm = {
  phoneNumber: string;
};

const SMSSendingFormSchema = z.object({
  phoneNumber: z
    .string()
    .regex(/^[0-9]{11}$/, { message: '電話番号（11桁）を入力してください。' }) // length validation
    .refine(isValidJapaneseMobileNo, '有効な電話番号を入力してください。'), // format validation
});

type SMSSendingFormProps = {
  sessionId: number;
  formIds: string[];
  setAccessLink: (link: string | undefined) => void;
  setStepper: (step: number) => void;
  onBack: () => void;
};

const SMSSendingForm: React.FC<SMSSendingFormProps> = ({ sessionId, formIds, setStepper, setAccessLink, onBack }) => {
  const { connectionType, setDocumentUploadStatus } = useUserContext();
  const [state, dispatch] = useReducer(reducer, initialState);
  const [modalType, setModalType] = useState<MODAL_TYPE>('send');
  const [smsStatus, setSmsStatus] = useState<SMS_STATUS | undefined>(undefined);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<SMSSendingFormForm>({
    resolver: zodResolver(SMSSendingFormSchema),
    mode: 'onSubmit',
    defaultValues: {},
  });

  const onSubmit: SubmitHandler<SMSSendingFormForm> = () => {
    // Reset sms status
    setSmsStatus(undefined);

    // Update modalType
    setModalType('send');

    // Reset retry state
    dispatch({ type: 'RESET' });

    // Open confirmation dialog
    setIsConfirmationModalOpen(true);
  };

  const sendSmsRequest = useSendAccess();

  const isRetryModal = modalType === 'retry';

  const handleConfirmSend = async () => {
    // Hide confirmation modal
    setIsConfirmationModalOpen(false);

    // Trigger API
    await sendSmsRequest.mutateAsync(
      {
        formIds,
        recipientNo: addJPCountryCode(getValues('phoneNumber')),
        sessionId,
      },
      {
        onSuccess: ({ accessLink }) => {
          queryClient.invalidateQueries({ queryKey: ['session', sessionId, 'documents'] });

          // Set sms status
          setSmsStatus('success');

          // Reset accessLink
          setAccessLink(accessLink);

          if (connectionType === AUTH_TYPE.External) {
            // Set document upload progress
            setDocumentUploadStatus(DOCUMENT_UPLOAD_PROGRESS.completed);
            setStepper(3);
          }

          setValue('phoneNumber', '');
        },

        onError: (error) => {
          if (error instanceof AxiosError) {
            // Set success status
            setSmsStatus('error');

            // Reset accessLink
            setAccessLink(undefined);

            if (connectionType === AUTH_TYPE.External) {
              // Set document upload progress
              setDocumentUploadStatus(DOCUMENT_UPLOAD_PROGRESS.metaDataCreated);
            }
            const errorCode = error.response?.data?.errorCode || undefined;

            if (errorCode.includes(ERROR_CODE.smsFailedWithRetry)) {
              setModalType('retry');
              startCountdown(RETRY_DELAY);
              dispatch({ type: 'TOGGLE_RETRY' });
              setIsConfirmationModalOpen(true);
            } else {
              setValue('phoneNumber', '');
            }
          }
        },
      },
    );
  };

  const getConfirmationModalContent = () => {
    if (isRetryModal) {
      return '再度SMS送信してください。\nまたはQRコード表示をご利用ください。';
    }
    return `${formatPhoneNumberDisplay(getValues('phoneNumber'))}\n入力いただいた電話番号で問題ないか確認してください。`;
  };

  const startCountdown = (seconds: number) => {
    let counter = seconds;

    dispatch({ type: 'SET_BTN_LABEL', payload: `${counter}秒後に再試行` });

    const interval = setInterval(() => {
      counter--;
      dispatch({ type: 'SET_BTN_LABEL', payload: `${counter}秒後に再試行` });
      if (counter < 0) {
        clearInterval(interval);
        dispatch({ type: 'RESET' });
      }
    }, 1000);
  };

  return (
    <>
      <form className="bg-interactiveHoverGrey p-10 flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <StatusModal
          id="status-modal"
          title={isRetryModal ? 'SMS送信に失敗しました' : '確認してください'}
          open={isConfirmationModalOpen}
          setOpen={setIsConfirmationModalOpen}
          content={getConfirmationModalContent()}
          buttons={[
            {
              text: 'キャンセル',
            },
            {
              text: state.btnLabel,
              onClick: handleConfirmSend,
              disabled: state.isRetry,
            },
          ]}
        ></StatusModal>

        <div className="flex gap-5">
          <Heading as="h3">電話番号を入力してください</Heading> <Badge variant="important" text="必須" />
        </div>
        <Text>携帯電話番号を入力してください。「SMSを送る」を押すと、撮影・送信用のURLを掲載したSMSが送られます。 ハイフン（-）は不要です。</Text>
        <div className="flex flex-row justify-start gap-5">
          <div className="w-1/2">
            <TextField
              {...register('phoneNumber', {
                onChange: (e) => {
                  // Reset sms status
                  smsStatus && setSmsStatus(undefined);
                  e.target.value = e.target.value.replace(/\D/g, '');
                },
              })}
              showError={Boolean(errors.phoneNumber)}
              maxLength={11}
              errorMessage={String(errors['phoneNumber']?.message)}
            />
          </div>
          <Button type="submit" icon={<ArrowIcon />}>
            SMSを送る
          </Button>
        </div>

        {smsStatus === 'success' && (
          <Message status="success" title="SMS送信しました" hasCloseButton={false}>
            届かない場合は電話番号をご確認のうえ、再度お送りください。
          </Message>
        )}
        {smsStatus === 'error' && (
          <Message status="danger" title="SMS送信でエラーが発生しました" hasCloseButton={false}>
            申し訳ございませんが、再度SMS送信いただくか、QRコードまたはEメールをご使用ください。
          </Message>
        )}
      </form>
      <div className="flex justify-center gap-3 mt-5">
        <div className="w-80">
          {smsStatus === 'success' && connectionType === AUTH_TYPE.Internal ? (
            <Button type="button" onClick={onBack} fullWidth icon={<ArrowIcon />}>
              次へ
            </Button>
          ) : (
            <Button variant="outlined" onClick={onBack} fullWidth icon={<ArrowIcon rotation="180" />} iconPosition="left">
              戻る
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

export default SMSSendingForm;
